import React from 'react';
import { Link } from 'react-router-dom';
import { Flame, CheckCircle, FileText, ArrowRight, Shield, Building2, Zap, FlaskConical, Eye, Droplets } from 'lucide-react';

const CoalFiredPowerPlants = () => {
  const keyChallenges = [
    {
      title: 'Mission-Critical Federal Support',
      description: 'Technical air quality oversight for federal and military heating facilities. We understand that for mission-critical infrastructure, environmental compliance is a baseline requirement for operational readiness.',
      icon: Shield
    },
    {
      title: 'Interior Utility Strategy',
      description: 'Practical permitting and compliance for utility cooperatives. We help you navigate the complex 40 CFR 63 Subpart UUUUU (MATS) and Title V landscape without compromising generation reliability.',
      icon: Building2
    },
    {
      title: 'District Energy & CHP Compliance',
      description: 'Specialized support for Combined Heat and Power (CHP) plants. We ensure that Interior Alaska\'s essential district heating assets meet all BACT and SIP requirements for the Fairbanks Serious Nonattainment Area.',
      icon: Zap
    },
    {
      title: 'MATS & NESHAP Technical Management',
      description: 'No-nonsense guidance on Mercury and Air Toxics Standards. We manage the testing protocols, data integrity, and reporting cycles so your team can focus on plant operations.',
      icon: FlaskConical
    },
    {
      title: 'Regional Haze & Visibility Defense',
      description: 'Developing technically sound "Reasonable Progress" strategies that protect legacy assets from restrictive federal mandates while meeting Alaska-specific visibility goals.',
      icon: Eye
    },
    {
      title: 'CCR & Ash Management Strategy',
      description: 'Managing Coal Combustion Residuals (CCR) with a focus on practical groundwater monitoring, fugitive dust controls, and long-term site-specific compliance.',
      icon: Droplets
    }
  ];

  const keyPollutants = [
    { name: 'Mercury (Hg)', impact: 'Neurological damage prevention' },
    { name: 'Hydrogen Chloride (HCl)', impact: 'Acid rain and respiratory issues control' },
    { name: 'Particulate Matter (PM)', impact: 'Respiratory and cardiovascular disease prevention' },
    { name: 'Carbon Monoxide (CO)', impact: 'Cardiovascular effects mitigation' },
    { name: 'Dioxins/Furans', impact: 'Toxic emissions control' },
  ];

  const neshapRequirements = [
    { title: 'Performance Testing', desc: 'Conduct tests within 60 days of completion, repeated periodically as specified by EPA' },
    { title: 'Continuous Monitoring', desc: 'Install and operate CEMS, COMS, and CMS systems to track emissions in real-time' },
    { title: 'Recordkeeping', desc: 'Maintain detailed records for at least 5 years, available to EPA upon request' },
    { title: 'Reporting', desc: 'Submit semiannual compliance reports with monitoring data and deviation summaries' },
    { title: 'SSM Plan', desc: 'Develop startup, shutdown, and malfunction procedures to minimize emissions' },
  ];

  return (
    <div className="min-h-screen" data-testid="coal-fired-page">
      {/* Hero Section - Coal Power with Dark Gradient */}
      <section className="hero-section relative text-white overflow-hidden" style={{
        height: '60vh',
        maxHeight: '720px',
        minHeight: '500px',
        padding: '5rem 0'
      }}>
        <style>{`
          .hero-section picture {
            width: 100%;
            height: 100%;
            display: block;
          }
          
          .hero-section picture img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            object-position: center bottom !important;
            display: block !important;
          }
          
          @media (max-width: 900px) {
            .hero-section {
              height: auto !important;
              max-height: none !important;
              min-height: 420px !important;
              padding: 3rem 0 !important;
            }
            .hero-section picture img {
              object-position: center center !important;
            }
          }
        `}</style>
        
        {/* Responsive Hero Image */}
        <div className="absolute inset-0 z-0">
          <picture>
            {/* Mobile Vertical (960x1200) */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/43beqzml_coal_clean_stacks_mobile_vertical_960x1200.webp"
              media="(max-width: 600px)"
              type="image/webp" />

            {/* Mobile Horizontal (960x640) */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/lyp5oir4_coal_clean_stacks_mobile_horizontal_960x640.webp"
              media="(max-width: 900px)"
              type="image/webp" />

            {/* Tablet (1536x900) */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/y816cw65_coal_clean_stacks_tablet_1536x900.webp"
              media="(max-width: 1200px)"
              type="image/webp" />

            {/* Desktop (2560x1200) */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/isnt15cd_coal_clean_stacks_desktop_2560x1200.webp"
              media="(min-width: 1201px)"
              type="image/webp" />

            {/* Fallback */}
            <img
              src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/isnt15cd_coal_clean_stacks_desktop_2560x1200.webp"
              alt="Clean-coal compliance–focused power plant in Alaska showing tall emissions-control stacks, conveyors, and rugged mountain terrain under a clear sky, representing Raven Environmental Consulting's permitting and regulatory expertise."
              loading="eager"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }}
              className="w-full h-full" />
          </picture>
        </div>
        
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg">
              Reliable Baseload. Expert Oversight. Field-Proven Compliance.
            </h1>
            <p className="text-2xl text-gray-100 mb-8 drop-shadow-md">
              Interior Alaska relies on coal-fired generation for winter security and baseload power. We provide the specialized air quality strategy required to keep these vital assets running clean, compliant, and reliable through the harshest conditions.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-orange-700 text-white rounded-lg font-semibold hover:bg-orange-800 transition-colors shadow-lg"
              >
                Consult with an Interior Specialist
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <a
                href="#key-challenges"
                className="inline-flex items-center px-6 py-3 bg-white text-orange-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                View Our Capabilities
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Fuel-to-Stack: The Raven Advantage</h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Operating a solid-fuel plant in Alaska is about more than just checking boxes—it's about managing a variable feedstock in a strict regulatory environment. Because our expertise extends from the coal geology to the stack monitor, we offer a "Fuel-to-Stack" perspective that is unique in the state.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We understand how fuel variability directly impacts combustion chemistry, boiler performance, and emissions control systems. Raven Environmental bridges the gap between the mine and the monitor, helping operators adjust their compliance strategies in real-time to maintain uptime without risking a permit violation.
            </p>
          </div>
        </div>
      </section>

      {/* Key Challenges - Burnt Orange Jewel Icons */}
      <section id="key-challenges" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Key Compliance Challenges
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise for MATS, Coal Ash, Regional Haze, and Title V compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyChallenges.map((challenge, index) => {
              const IconComponent = challenge.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border-t-4 border-orange-700 hover:-translate-y-1">
                  {/* Jewel Style Icon - Burnt Orange */}
                  <div className="bg-orange-700 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{challenge.title}</h3>
                  <p className="text-gray-600">{challenge.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NSPS Requirements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">40 CFR 60 - Federal Regulatory Requirements (NSPS)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Subpart Da</h3>
              <p className="text-gray-600 mb-4 text-sm">Electric Utility Steam Generating Units</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>SO2, NOx, PM limits for utility boilers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>CEMS requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Performance testing protocols</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Subpart Db</h3>
              <p className="text-gray-600 mb-4 text-sm">Industrial-Commercial-Institutional Units</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Standards for district heating & CHP</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>SO2, NOx emission limits</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Fuel quality monitoring</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Subpart A</h3>
              <p className="text-gray-600 mb-4 text-sm">General provisions for all affected facilities</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Compliance testing requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Monitoring and recordkeeping</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Notification and reporting</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Subpart Y</h3>
              <p className="text-gray-600 mb-4 text-sm">Coal preparation and processing plants</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Particulate matter standards</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Opacity limits</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Control technology requirements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Technical Specifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Box 1: Emission Standards & Reporting */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-orange-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Emission Standards & Reporting</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">MATS Compliance (40 CFR 63 Subpart UUUUU)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NSPS Subpart Da (Utility Units) & Db (Industrial Units)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Title V Permit Strategy & Negotiations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">CEMS/COMS Data Auditing & Accuracy</span>
                </li>
              </ul>
            </div>

            {/* Box 2: Operational Oversight */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-orange-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Operational Oversight</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Fuel-to-Emission Synergy Analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Fairbanks SIP & PM2.5 Compliance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">CCR Rule Oversight (40 CFR 257)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Annual Compliance Certifications (ACC)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Subpart DDDDD */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              NESHAP Subpart DDDDD - Major Sources
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <p className="text-gray-600 mb-6 leading-relaxed">
                Applies to major sources capable of emitting 10 tons or more per year of any single HAP or 25 tons or more per year of any combination of HAPs.
              </p>
              
              <h3 className="text-xl font-bold text-gray-900 mb-4">Work Practice Standards</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Periodic Tune-Ups</h4>
                  <p className="text-sm text-gray-600">Regular tune-ups to optimize combustion efficiency and reduce emissions</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Energy Assessments</h4>
                  <p className="text-sm text-gray-600">Identify opportunities for improving energy efficiency and reducing emissions</p>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Monitoring Systems</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Shield className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">CEMS - Continuous Emission Monitoring Systems</span>
                </li>
                <li className="flex items-start">
                  <Shield className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">COMS - Continuous Opacity Monitoring System</span>
                </li>
                <li className="flex items-start">
                  <Shield className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">CMS - Continuous Monitoring System</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CAM Requirements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            40 CFR 64 - Compliance Assurance Monitoring
          </h2>
          <div className="bg-gray-50 p-8 rounded-lg max-w-4xl mx-auto">
            <p className="text-gray-600 mb-6 leading-relaxed">
              CAM provisions typically apply to coal-fired boilers due to the operation of control devices such as baghouses. Required for emission units with pre-control emissions exceeding 100 TPY.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Key Requirements</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Monitoring system design and performance criteria</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Quality assurance and calibration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                    <span>5-year recordkeeping requirements</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Periodic reporting to EPA/ADEC</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Implementation</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs">1</div>
                    <span>Initial assessment of control devices</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs">2</div>
                    <span>Design tailored monitoring systems</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs">3</div>
                    <span>Train personnel</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs">4</div>
                    <span>Maintain continuous monitoring</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Title I Permits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-4xl mx-auto border-t-4 border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-8 h-8 text-gray-700 mr-3" />
              Title I Permit Requirements
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Coal-fired power plants must comply with comprehensive permit requirements including:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Best Available Control Technology (BACT) limits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">EPA Prevention of Significant Deterioration (PSD) permits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">State Implementation Plan (SIP)-approved permits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Ambient air quality standard compliance</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Expert Coal-Fired Power Plant Compliance Support</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Navigate complex regulatory requirements with confidence. Our team provides comprehensive support for coal-fired power plant permitting and compliance.
          </p>
          <Link
            to="/contact/coal-fired"
            className="inline-flex items-center px-8 py-4 bg-white text-gray-800 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Get Expert Coal-Fired Compliance Support
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CoalFiredPowerPlants;
